import React from "react";
import { Button } from "@/components/ui/button";
import {
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import { Select } from "@/components/ui/select";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  pageSize: number;
  totalRecords: number;
  hasNext: boolean;
  hasPrevious: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  isLightMode?: boolean;
}

const pageSizeOptions = [10, 25, 50, 100];

export function Pagination({
  currentPage,
  totalPages,
  pageSize,
  totalRecords,
  hasNext,
  hasPrevious,
  onPageChange,
  onPageSizeChange,
  isLightMode = false,
}: PaginationProps) {
  React.useEffect(() => {
    if (pageSize > totalRecords && totalRecords > 0) {
      const validPageSize =
        pageSizeOptions.filter((size) => size <= totalRecords).pop() || 10;

      if (validPageSize !== pageSize) {
        onPageSizeChange(validPageSize);
      }
    }
  }, [totalRecords, pageSize, onPageSizeChange]);

  const handlePageSizeChange = (newSize: number) => {
    onPageSizeChange(newSize);
    onPageChange(1);
  };

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (currentPage > 3) {
        pages.push("...");
      }

      // Show pages around current page
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (currentPage < totalPages - 2) {
        pages.push("...");
      }

      // Always show last page
      pages.push(totalPages);
    }

    return pages;
  };

  const startRecord = (currentPage - 1) * pageSize + 1;
  const endRecord = Math.min(currentPage * pageSize, totalRecords);

  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      {/* Records info and page size selector */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
        <p className="text-black-500 text-xs font-medium">
          Showing {startRecord} to {endRecord} of {totalRecords} records
        </p>
        <div className="flex items-center gap-2">
          <label
            htmlFor="pageSize"
            className="text-black-500 text-xs font-medium"
          >
            Per page:
          </label>
          <Select
            value={pageSize.toString()}
            onValueChange={(value) => handlePageSizeChange(Number(value))}
          >
            <SelectTrigger
              className={`border-black-500 !h-6 w-[65px] border pr-0 pl-2 ${isLightMode ? "bg-white" : "bg-black-950 text-white"}`}
            >
              {pageSize.toString()}
            </SelectTrigger>
            <SelectContent
              className={`border-black-500 border ${isLightMode ? "bg-white" : "bg-black-950"}`}
            >
              {pageSizeOptions.map((size) => (
                <SelectItem
                  key={size}
                  value={size.toString()}
                  disabled={size > totalRecords}
                  className={`hover:bg-black-500 border-black-500 disabled:cursor-not-allowed disabled:opacity-50 ${isLightMode ? "text-black" : "text-white"}`}
                >
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Pagination controls */}
      <div className="flex items-center gap-2">
        <Button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={!hasPrevious}
          variant="minimal"
          className="h-8 w-8 p-0 text-white hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-30"
        >
          <i
            className={`ri-arrow-left-s-line text-lg ${isLightMode ? "text-black-500" : "text-white"}`}
          ></i>
        </Button>

        <div className="flex items-center gap-1">
          {getPageNumbers().map((page, index) => {
            if (page === "...") {
              return (
                <span
                  key={`ellipsis-${index}`}
                  className={`px-2 text-xs ${isLightMode ? "text-black-500" : "text-white"}`}
                >
                  ...
                </span>
              );
            }

            return (
              <Button
                key={page}
                onClick={() => onPageChange(page as number)}
                variant="minimal"
                className={`h-8 min-w-[32px] px-2 text-xs ${
                  currentPage === page
                    ? `${isLightMode ? "bg-white text-neutral-800 hover:bg-white/90" : "bg-black-950 hover:bg-black-950/90 text-white"}`
                    : `${isLightMode ? "text-neutral-500 hover:bg-white/10" : "text-gray-300 hover:bg-white/10"}`
                }`}
              >
                {page}
              </Button>
            );
          })}
        </div>

        <Button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={!hasNext}
          variant="minimal"
          className={`h-8 w-8 p-0 text-white hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-30 ${isLightMode ? "text-black-500" : "text-white"}`}
        >
          <i className="ri-arrow-right-s-line text-lg"></i>
        </Button>
      </div>
    </div>
  );
}
